import { TestTranscripts } from './test-transcripts';

// Synthetic transcript library (ENG-5403) — the presenter's prop for demoing
// multi-visit patient journeys via the Eluve Admin Panel's transcript
// injection tool. Ported from Orchard's injection picker (orchard-ehr,
// ENG-4966 / ENG-5061), minus the plan-shape taxonomy: journeys are grouped
// and filtered by specialty/problem type instead. Version-controlled, no DB:
// a typed array the picker searches, groups by journey, and orders by visit.
// Picking an entry fills the (still-editable) transcript textarea; injection
// itself is unchanged.
//
// Authoring intent (inherited from Orchard): Scott's visit 1 (rich) is the
// canonical "good path" — a real de-identified Myodetox eval, verbatim, with
// heavy ASR noise in the back half; that realism is intentional. Visits 2–4
// advance the arc so extraction yields a clean improving trend on the
// transcript-native measures. The visit-1 thin variant is the two-paths beat:
// a chitchat eval that — correctly — yields visibly less structured output.
// Raymond (type 2 diabetes) holds an HbA1c control band with a
// drift-and-review arc; Nadia (chronic migraine) is a preventive baseline
// punctuated by a trigger flare and a medication-overuse pattern. All
// time-series values are stated in-visit — a lab result is spoken by the
// clinician, never a side artifact.
//
// The pre-existing single-visit example conversations (`TestTranscripts`)
// are wrapped as ungrouped entries at the bottom so they stay injectable
// from the same picker.

export type TranscriptVariant = 'rich' | 'thin';

/** Specialty / problem-type facet behind the picker's filter chips. */
export const TRANSCRIPT_SPECIALTIES = [
  'Physical therapy',
  'Primary care',
  'Neurology',
  'Oncology',
  'Pelvic health',
] as const;

export type TranscriptSpecialty = (typeof TRANSCRIPT_SPECIALTIES)[number];

export type TranscriptLibraryEntry = {
  /** Stable id, e.g. "scott-v1-rich". */
  id: string;
  /** Groups entries into one patient arc; unset for single-visit entries. */
  journeyId?: string;
  /** Short who-this-is label for the picker. */
  persona?: string;
  /** 1-based visit number; the picker orders by this. */
  visit?: number;
  /** Protocol phase this visit sits in (narrative, mirrors the eval's 5-phase model). */
  phase?: string;
  /** "rich" = full clinical detail; "thin" = chitchat, degrades on purpose. */
  variant?: TranscriptVariant;
  /** Picker row title. */
  title: string;
  /** One-line gloss shown under the title. */
  summary: string;
  /** Specialty / problem-type facet for the filter chips. */
  specialty: TranscriptSpecialty;
  /** Free-text search terms (matched against title + summary + these). */
  tags: string[];
  /** The transcript body that lands in the textarea. */
  text: string;
};

export type TranscriptJourney = {
  id: string;
  /** Group heading in the picker. */
  title: string;
  /** Secondary line under the heading. */
  subtitle: string;
  /** Specialty facet for the journey; drives the picker's badge and filter. */
  specialty: TranscriptSpecialty;
};

export const TRANSCRIPT_JOURNEYS: TranscriptJourney[] = [
  {
    id: 'scott',
    title: 'Scott — low back + shoulder',
    subtitle:
      '43M · post-stabilization shoulder, longstanding lumbar stiffness · 4-visit arc',
    specialty: 'Physical therapy',
  },
  {
    id: 'raymond',
    title: 'Raymond — type 2 diabetes',
    subtitle: '57M · ongoing HbA1c management, drift-and-review · 4-visit arc',
    specialty: 'Primary care',
  },
  {
    id: 'nadia',
    title: 'Nadia — chronic migraine',
    subtitle:
      '34F · preventive baseline + flare and medication-overuse events · 4-visit arc',
    specialty: 'Neurology',
  },
];

// ---------------------------------------------------------------------------
// Visit 1 (rich) — VERBATIM de-identified Myodetox eval. Speaker A = clinician,
// Speaker B = patient (Scott, 43M). This is the library's seed entry; do not
// paraphrase. The back half (manual-therapy portion) carries heavy ASR noise —
// that realism is intentional.
// ---------------------------------------------------------------------------
const SCOTT_V1_RICH = `Speaker A: What else you got going on beside this today?
Speaker B: Had some meetings this morning and then yeah, I think we're continuing just a shadow throughout the day.
Speaker A: Okay, cool, cool. Sweet. Other than that, how was your weekend? Anything fun?
Speaker B: My daughter's one year birthday is coming up this coming weekend, so we did some prep for that and bouncy castle.
Speaker A: Okay, that's fun. Nice. Nice. How big the biggest. The party gonna be like what's the expected turnout?
Speaker B: It's dumb. It's like 40 or 45 people. Like a one year old that, you know, she just, she crawls and she's cute and we're just gonna invite the whole neighborhood, so.
Speaker A: Yeah, yeah, sounds fun. But she probably won't remember.
Speaker B: This is like half it is like close friends and family and the other half is like people that we've met on walks throughout the neighborhood that also kids and just like an olive branch to say like, hey, like we live near each other. Should we hang out maybe? There we go. Yeah. So we'll, we'll see how it goes. It should be fun.
Speaker A: Yeah. Yeah. It sounds like it's gonna come together and be a cool experience for all.
Speaker B: Yeah.
Speaker A: I'm assuming it's okay for us to run a loop in the background here. Yes, absolutely. Perfect.
Speaker B: I mean, what's a loop?
Speaker A: It's this cool little tool that we got. It helps us transcribe, you know, all of our notes. You know, actually help us be a little bit more attentive to our conversations here as we're track some of the things that we do here in session. Cool.
Speaker B: Yeah, I actually saw that in the intake forum aloove. Yeah, sounds cool.
Speaker A: Dope. Dope. Yeah. Speaking of you, I appreciate you filling out the intake form. Super important for me to understand your goals and what you want to get back into as we get started here. The more I understand your story, the better I can actually help you out here. Cool. So what we'll do here is we'll speak a little more about your symptoms and we'll be really specific about your goals and what you want to get back into. And then after that we'll get started on some hands on treatment. But a potential plan actually moving forward to actually improve your shoulder and your mobility as well.
Speaker B: Great.
Speaker A: What's something that you're hoping for to get out of today's session?
Speaker B: So I, in general, I'm very inflexible. Like it's been, I don't know, 10 years since I've been able touch my toes. Like this is the part where there's like, there's no pain, and, like, it gets, like, somewhat painful and, like, moderately painful. And after I warm up, I can probably get, like, down a bit farther, but very tight hamstrings. Probably, like, every four to six months, I throw out my back doing something like put on a pair of pants.
Speaker A: Okay, got it, Got it.
Speaker B: So it's not uncommon for me to be, like, pretty much bedridden for, like, a day to two days where it's just really painful. That hasn't happened in the last six months. I think it's, I guess, is because, like, I'm more mobile with my daughter. Like, I'm, like, crawling around with her and, like, you know, picking her up and stuff. And maybe that's just, like, more activity than my normal sedentary lifestyle.
Speaker A: Yeah.
Speaker B: And then specifically, also for today, like, I have, like. Just, like, it doesn't actually hurt too much today, but, like, lifting up my right arm is, like. It has been, like, painful right here. I did have shoulder surgery on this arm.
Speaker A: Yeah.
Speaker B: Like, eight years ago, but I. I always sleep with, like, my arm above my head, and, like, sometimes I wake up and it's just, like, it feels weird. And there's been a pretty much pretty consistent. For the last few weeks, except for today, it has been, like, kind of painful when I, like, raised my hand like that.
Speaker A: What led you to the. The initial surgery here, Scott?
Speaker B: I had. I had a torn labrum.
Speaker A: Yeah.
Speaker B: A bit of a long story, but, like, when I was a kid, I could, like, dislocate my shoulder, and I thought that was fun to do, and that was probably not a good thing
Speaker A: for me to do.
Speaker B: And then there were just, like, a couple times throughout my life where, like, it got dislocated and I just got sick of it. And I finally decided to elect to get surgery. And then I had PT for, like, six months after it, like, twice a week, and it got my mobility back, but, like, the strength really never came back, and I just don't have the time or I don't allocate the time to work out anymore. So I. I have a membership to a gym. I go there, like, once a month, which is not nearly enough. So.
Speaker A: Yeah.
Speaker B: So I really am a shell of my former athletic self.
Speaker A: Got it. Yeah. It sounds like you. You have, you know, that injury, like, that recurrent, like, dislocating type of mechanism, the surgery, you know, fix stuff. You know, a lot of things we might have done, like, you know, full range of motion, but you haven't gotten, you know, the full capacity of the shoulder?
Speaker B: Yeah.
Speaker A: And in what ways does it bother you other than kind of raising your arm? Certain things on day to day that this bugs you with?
Speaker B: I mean, we had our company retreat a few weeks ago and it was even hard for me to play table tennis because I couldn't flip my arm like that. But I mean, when I just type at my keyboard, it's in a range that is very comfortable, so it's not a big deal at all. But I would love to be able to easily play pickleball or tennis. I throw my kid in the air and I can do that like three or four times before I'm just like, okay, maybe I don't want to do it a fifth time because who knows if I'm gonna drop her somewhat, tongue in cheek. But also like, you know, there's only so many times I can throw a 25 pound ball of, you know, cute
Speaker A: kid in the air.
Speaker B: Yeah, yeah, totally.
Speaker A: And yeah, I mean, it sounds like, you know, there's the things that are on your mind in terms of the shoulder and how this. Actually, it's kind of like more so being like present during some of those moments that matter between like, you and your kid. Did I get that right? Yeah. What else?
Speaker B: I mean, like, just like we want to have three or four kids if, like, if all goes well and just kind of projecting out like where my body used to be, where it is now, how long it's going to take for like two to three more kids to arrive. It's like I. I just want to make sure that I'm not continuing to get worse and I can at least stabilize and hopefully improve. But.
Speaker A: Yeah, yeah, yeah. You said you shot out of two or three more kids. Oh, man. Impressive. Impressive. Okay.
Speaker B: Okay.
Speaker A: You got big plans?
Speaker B: Yeah, we have one and we don't have a second one on the way. But like, the first one's been awesome and there's a good chance the second one is not. So that might change.
Speaker A: The first one's already set a high bar. Now with regards to some of those discomforts that you're feeling at the shoulder, are there certain things that tend to make it better, like certain stretches or certain exercises or just getting out of that position tend to be pretty.
Speaker B: Yeah. I think like, the, the big thing is, is when I, like, if I, if I don't sleep on, like, this is like very comfortable for me to sleep, but like, I'll just wake up and it'll just be in a weird position. So if I somehow in the middle of the night, like Sleep with my arm down or end up falling asleep on my back. Then, like, I'll wake up and it'll feel a lot better. I don't try to. I haven't tried to, like, actively do any type of stretches or bands or things. I usually just, like, stop inflaming it by not moving and by not, like, putting it into, like, a danger zone and. And then it usually feels fine a bit later.
Speaker A: Got it.
Speaker B: But it's not. I mean, to me, it doesn't seem like it's fixing the problem. It's just, like, ignoring it.
Speaker A: Yeah. Sounds like it's more like ignoring or, like, avoiding those particular motions.
Speaker B: Yeah.
Speaker A: What about the back and your sense of inflexibility? What I'm hearing is when you're bending down, you feel fairly immobile. What else tends to trigger some of those complaints?
Speaker B: So, I mean, like, even, like, kind of, like, pulling my toes back and like, leaning forward all just, like. There is, like, a very activated, like, tinge of pain in my, like, hamstrings. And I find, like, throughout the day, like, I. If I, like, rotate my neck, I can either, like, it feels like it kind of just, like, pops a few things in my upper back, which ends up feeling, like, really good relief. Like, it's not like I'm in pain, but there's just, like, once I do this a couple of times throughout the day, it'll just, like, really make me feel much more relaxed. But, yeah, lower back tightness, like, hamstring tightness, just makes it. Yeah, just makes it hard for me to, you know, want to be mobile.
Speaker A: Yeah, yeah, yeah. You've. You've been fairly inflexible for quite a while. Is that correct?
Speaker B: Oh, at least 10, 15 years. Yeah. Like, I. I played all the sports growing up. Like, I played golf, tennis, basketball, baseball, soccer, indoor soccer, like, everything. And I played that most of those through middle school and some in high school, but I also grew, like, six inches over the course of, like, six months. And that's when, like, my inflexibility like, really started. And through those sports, like, I was continuing to stretch. But then certainly, like, in college and post college, like, my intramural day started waning quite a bit. And, yeah, by, like, I'm 43 now, and by my mid-30s, I, basically, because of the shoulder surgery, stopped playing volleyball, stopped playing, you know, basketball. And now, yeah, I play, like, pickleball and, like, maybe one round of golf. But you year. Yeah, but I wouldn't, like, if I could allocate the time for it. I Would love to be able to play sports like every week. Yeah.
Speaker A: Let's say like the, the shoulder and the back, you know, like pain and inflexibility improves or like goes away in the next three to six months. What does that do for you? Like, what are you doing more?
Speaker B: Yeah, well, I mean, these are, I think in, in conjunction with other stuff. It's just I don't prioritize my physical health. And so these are things that like, when I do want to do something, it's like, okay, I'm making the time for it, but like, oh shit, my, my shoulder feels bad or like, oh, like, you know, I, I feel like I really need to like deeply warm up before I do anything, which is probably not a bad thing, but it's just, it's just one more mental burden to get in the way of like doing something active and fun.
Speaker A: Got it, Got it. Is this more so. Is the intention of this more so like when you have more time and, and energy, you know, dedicate and commit to some of these, you know, things such as exercise and racket sports, that you feel better and more confident getting back into things like that.
Speaker B: I mean, every time I do have the chance to play racquet sports, I am just in such a great mood the next day because it's so fun to me. But I just saying these things out loud makes me realize how much I've focused on work and like family time and just not, you know, doing things that I used to find fun that are, I think important for me to, you know, make time for.
Speaker A: Yeah, yeah. Why do you feel like that's the case in points of making time for that again,
Speaker B: like why, why have I not made time?
Speaker A: Why is it even important for you?
Speaker B: Yeah, I think like just being like, I'm a middle aged dude now, so like if I don't prioritize this stuff, like it's not gonna magically get better. It's not like I'm you know, a teenager in my twenties where like bodies just like bounce back. Like it's now a project that like needs to be continually push forward. Otherwise. Yeah, like I, you know, I could imagine being 70 and being, you know, really frail and even more immobile and have like bad posture and you know, you take one tumble down some stairs or something and it's. You go from having self sustaining life to like now needing care quite a bit.
Speaker A: So you worry about what you're feeling now compounding things to create issues. Yeah. So here's what I've gathered from you. So Far. You've had this shoulder issue in the past where, you know, dislocated, but you've had a stabilization surgery on the shoulder. This was around eight years ago. And then since then, you went through a course of pt. You've gotten, you know, a lot of just general motion back, but we haven't restored much of the strength. The low back and your general inflexibility, it's been ongoing for years. And the combination of these couple of things takes away from some of your enjoyment of, you know, hanging out with your kid and tossing things around and also getting back to your sports more. Did I get that all correct?
Speaker B: Yeah.
Speaker A: Is there anything else that you feel like I may have missed that you want to bring up?
Speaker B: Sometimes my neck does get into, like, a. Like, a bit of a stiff. I don't know if they call it, like, tech neck or something, but, like, I. It's, like, actually hard for me to turn my head, and, like, I can only move my head like this, and then it, like, it cracks, and then I can actually, like, move it. I don't know if that is a really problematic thing because it is decently easy to solve with just, like, a little bit of motion, but that happens probably twice a week.
Speaker A: Okay, noted. Noted.
Speaker B: Yeah, I'll keep all that, you know,
Speaker A: it was a motion kind of, like, type of, like, sensitivity in mind as we're taking, like, a shoulder as well.
Speaker B: Cool.
Speaker A: Cool. For today's session, we'll be able to touch upon, you know, a little bit the shoulder, a little bit of the back, but if you have, like, a higher card, just so that I can make sure that I can most be most of the efficient for your time? Yeah. Is there a area that you would for sure want to see, like, more change or, like, more of a plan for?
Speaker B: I mean, my shoulder is fine today, which is somewhat annoying.
Speaker A: It's like bringing your car to a mechanic.
Speaker B: Yeah. So I don't know if you can take, like, maybe a very brief look at that, but, yeah, my lower back and hamstrings and flexibility are probably, like, the things that are most clear to measure and have an ongoing problem.
Speaker A: Got it, Got it. All right, so from here, what I'm going to have you do is if you don't mind taking off your socks and shoes, Also your shirt as well. All right, so go ahead and point to where you'll generally feel that shoulder hitting.
Speaker B: It's usually, like, right. It's like, right here.
Speaker A: Got it, Got it. All right, from here, nice and relaxed. Just take a couple of Marches here just to reset your posture and go ahead and come to a stop. Okay. I'm just going to note out a few things that maybe not be relevant to sensations, but right off the bat, your left shoulder is actually a little bit higher than your right side. So there could be some pre existing tension on the right side that could be an explanation of why that right shoulder can also feel restricted. The other thing that I'm noting is your hip is shifted a little bit more towards your right side as well. Now the, the stiffness and the tension that you'll feel in your lower back, is it usually pretty symmetrical? Is that from one side versus the other?
Speaker B: It's. Yeah, I think it's usually pretty symmetrical. I think both. Yeah, both legs and back, like it seems to be pretty uniform.
Speaker A: Okay, noted, Noted. Okay, from here, let's guide you through some arm motions. Both arms at the same time. We're going to reach up. Anything notable in this position?
Speaker B: No.
Speaker A: Okay, let's go. Elbows out to the side about turning down and up. Anything from a side to side perspective,
Speaker B: there's a 1 out of 10 pain like here compared to my left side. So yeah, my right side has like a little tightness or something.
Speaker A: And is that reminiscent of some of the symptoms or like area of symptoms that you'll feel sometimes?
Speaker B: Yeah, I think that like doing this is like, it is a very mild version of like when I was lifting my arm when there was a problem like over the last few weeks. So like this, this kind of forces the issue a little bit, it seems.
Speaker A: Got it. Noted. That's useful. What about reaching up your back, one arm at a. Go ahead. Anything.
Speaker B: Also that just took a little bit in that same spot from the last exercise.
Speaker A: And what about reaching downward?
Speaker B: This is fine.
Speaker A: Okay, that's fine. Okay. And let's do the same thing. This back, anything here on this side,
Speaker B: I feel like I have more range.
Speaker A: You do.
Speaker B: And there's a little. It's just like a stretch, but not, not necessarily cane.
Speaker A: And there is a difference in the way that I usually count, that is how many different like spinal segments you're in your body. So your, your left arm is flexible by around four spinal segments, which is about like this much from here. Let's have your feet together. Go ahead and reach down towards your toes. See how far down you can reach.
Speaker B: So I can push harder. But this is where it kind of like caps out and like my legs are already like shaking a little bit.
Speaker A: Got it? Yeah. And the thing that I do see is when is it okay. If I take a picture of you to show you exactly what I'm catching here. Okay. Go ahead and stand back up. So you. Good, good curvature around, like your, your mid back area. But once we got down to like, your lumbar spine, this is relatively like pretty straight. And this could explain why you feel some of you. Some of the pulling at like the hamstring. Yeah. Do you also feel it pulling in your low back area or is it more hamstring?
Speaker B: Oh, yeah, no, it's. It's like more pain is here, but there's definitely some tightness here.
Speaker A: Got it, got it.
Speaker B: Yeah. And I find, like, I've tried doing different stretches. I have no idea how to, like, make that rotate at all.
Speaker A: Yeah, yeah, yeah, yeah. It's. It's fairly locked up there and like, there's going to be a few things that we can do to, you know, start to improve some of the mobility as well. From here, let's have hands on hips.
Speaker B: And what about leaning back, Feet together or.
Speaker A: Let's go feet together? Yeah, anything when you're doing that,
Speaker B: there's like a little like. Yeah, I feel like I can just like almost like crack my neck. Yeah, that just feels good, honestly.
Speaker A: Good stretch. And then from here, let's bend to the side. Anything coming up for you in the back in terms of tightness?
Speaker B: Feels good.
Speaker A: Okay, let's go the other way.
Speaker B: Feels good. Isn't like a good stretch?
Speaker A: Yeah. Cross your arms. Let's compare that to rotating. So go ahead and turn as far as you can and then rotate towards your right. Anything that you know from a side
Speaker B: to side basis also. So you look good, strategic, cool.
Speaker A: Nothing huge like popped up for me and some of those small motions. So from here, let's check some hip mobility real quick. Okay, let's go ahead and set off here. So what I'm going to have you do is you're going to sit onto your butt.
Speaker B: Yep.
Speaker A: And you're going to go into this position, knees all the way to one side with your hands up. So. Yep.
Speaker B: Like this. Yep.
Speaker A: Anything, you know, tightens pressure.
Speaker B: My ankle hitting the floor is painful, so.
Speaker A: Ank. But perhaps of pressure.
Speaker B: Yeah, there's like a little bit of tightness here. But these are like when I do some stretches at home, like I do like this 90 stretch.
Speaker A: Yeah, yeah. It's a good one. Yeah, yeah, for sure, for sure. And the things that I'm seeing that are telling me that you're running into some restriction here is ideally, if you're in this position, one like Your butt cheek is closer to the ground here. I mean, you're fairly close, but it doesn't necessarily, like, touch the 300. Yeah, yeah. That tells me that we have some left hip internal rotation restriction. Let's go the other way. Similar here. Right. The right butt. You could get a little bit closer to the core, ideally, but on average, you know, tall and, like, you guys might have internal rotation issues, and that's secondary to your predisposition. Shouldn't let me know what they. From here, let's have you go ahead and stand back up. Let's come on back. Can you have a seat for me? I just want to do a couple more press up the shoulders. Okay, going to the left. I'm just going to borrow your left arm first. We have a good breakfast. Two hands. Any pain and pressure around that area
Speaker B: when you're pushing it all the way down? There was a little bit of pressure.
Speaker A: A little bit of pressure. Right.
Speaker B: Same as the left.
Speaker A: Okay, Same as the left. Okay. From here, bring your arm out to this position. Anything in this range here. And if you were to slowly lower it, you can catch in the shoulder if it's okay.
Speaker B: Feels good.
Speaker A: Hold your arms strong in this position. Don't let me push inward. Hold, hold. No. Okay, from here, let me get you a face coverage. Wherever you lay face down. I'm just going to press onto a couple of the joints. Pull back just to get a full idea of what's moving once in a while. Cool. Go ahead and lay face down. Okay. I'm going to start off a little bit higher than where you'll feel some of your stiffness. You let me know if I reproduce any of your, like, pressures or just jumpers or textures.
Speaker B: Okay.
Speaker A: There.
Speaker B: That last one was a little. Little tight.
Speaker A: Yeah, it felt tight. You go ahead and lay piece of pressure. Were you about to say something?
Speaker B: Yeah, Those last few were like. I mean, there was a little bit of pain there.
Speaker A: Got it, Got it. Yeah. It makes sense given how, you know, they're your lumbar, like your low back segments, they're quite stiff, and they don't have, you know, much room to move when I'm pressing around those. What do you want me to do? Let's have you lay face down.
Speaker B: Okay.
Speaker A: I'm just going to check the shoulder option here. Internal rotation on this side is roughly around like, 45 degrees here. Not bad. Ideally, we're shooting for around like, 60, 70 degrees, but we're going to see if that's different compared to the other side. External rotation on your Right side is nice and healthy. Internal rotation on this side is actually like pretty healthy. 7 degrees external rotation is really good as well. Cool. Go and have a seat. We'll recap and talk about what's going on.
Speaker B: Okay.
Speaker A: We'll come up with a plan for today. Cool. So one sec, I'm just going to jot down a couple minutes here. I don't know if I got the measurements. All right. So the things that we found that were especially relevant for you today was, you know, pertaining with you, your, your low back, your toe touch was actually quite limited. But also when I had you sit onto your butt and I had you wave your knees from side to side, both hips actually lack some internal rotation. And also when I had you laying on to your stomach and I pressed down onto those different low back spinal segments, it actually reproduced some of the stiffness and also some painful sensations. All of that tells me that you're dealing with a case of, you know, someone who is anatomically pretty, like tall, lanky, and compounding that, you know, onto the, the normal wears of life. Your back's just stiffened up because you're predisposed to an anatomical variance that's tightened up even more over time because there hasn't been potentially as much like mobility that's prioritized in life. Okay. The other thing is your shoulder, your shoulder is limited. With shoulder internal rotation, as when we were doing this what we call kind of like a scarecrow test. This didn't feel too nice, you know, at the shoulder. But also when you reach behind your back, that was also limited by four spinal segments as well. When I had you on the table and I assessed this position, that was also relatively tight. That's around like 45 degrees opposed to 70 degrees. All those tell me, you know, you've had this history of the shoulder state stabilization surgery that actually haven't restored like full on range of motion for. And because of that, since you haven't restored, you know, a lot of those motions sleeping on that shoulder in this position, if you don't have as much shoulder internal rotation, that can actually get quite achy. And also if you don't have as much rotation of the shoulders going to overhead positions, that can also reproduce, you know, some of the symptoms that, that through porting. That's why when you're carrying a kid and kind of like tossing things up and down and also bending forward because you have the stiffness in those different areas, you're feeling a little bit limited there. That was a lot of info. But what questions do you have for me so far?
Speaker B: What's it going to take for me to be as athletic as I was in my 20s?
Speaker A: Yeah. Yeah. It's going to take something that you have. You've told me that you haven't had much time to dedicate towards this investing time for yourself to. In order to prioritize some of these movement and strengthening qualities. But I can tell you what a good starting point could be. What we'll do today is we'll focus on some myofascial work around the front of the shoulder. We're going to press down to the joints at the shoulder and the back to restore some rotation and also some forward bending pec. Afterwards, we'll take it. We'll take it out to the movement space where I'll show you a couple of drills to actually continue to restore those motions. So that way you can keep playing with your kid and keep playing with racket sports as fast as you can and also get a little bit more restful.
Speaker B: Sounds good.
Speaker A: Cool. Sweet. Any questions for me? No. Cool. Here's what we'll do. I'm gonna have you lay face down first. I'm going to go into your. Okay. And afterwards I'll have you flip over to your shoulder.
Speaker B: Sure.
Speaker A: So we're going to lay face down. I'm just going to put a pillow going to t those just under your shins. See how. What's your kid's name?
Speaker B: Her name is Alexandra. She goes by Al.
Speaker A: Strong name. Just gonna put a towel over your. Have you not had enough time to prioritize your movement, health and exercise because of the business, with the business and work? Like, what's. What's behind that so far?
Speaker B: We're part of a startup and we have people across seven time zones in four countries. So I've kind of been the context bridge across town. Time zones.
Speaker A: Yeah.
Speaker B: And I mean, I love what I do, so it's also hard for me to stop working.
Speaker A: Yeah.
Speaker B: And it just turns. And this is the same way. My, my previous startup is it was other remote first startup across different time zones. Set of people in apac, we had people in Europe there. And it was always, just always been being on and trying to, you know, prevent people stalling out. If they have a question in the middle of the night US time, I can usually answer that. And then they don't have to, like wait, you know, full 24 hours for them to, you know, keep doing the thing that they're trying to do.
Speaker A: Yeah.
Speaker B: But it definitely comes as a toll. And I think to some level. It's also just like an excuse that like, I do prefer to work than to work out and I just need to really allocate the time for it.
Speaker A: Yeah, you're a busy man, but you also, you know, enjoy what you do to, you know, perhaps, you know, at some certain points it doesn't really feel like work for you.
Speaker B: I mean, we have a pretty great team.
Speaker A: Nice.
Speaker B: And feel like it's
Speaker A: feels nice.
Speaker B: I mean this is, this is like a golden age of software right now. It's like you can do so much stuff with such a small team. Just being able to like hear the, the anecdotes from the doctors that we help and the nurses that we know that, you know, it's actually making their day better seeing like unsolicited comments, like Reddit threads, people using our product.
Speaker A: Oh, nice. Yeah, you can, you can really like hear, you know, the impact that you're making on these, on these people.
Speaker B: Depends on my food. Six weeks, three months. I'm about to take off.
Speaker A: So.
Speaker B: Lando, this Friday.
Speaker A: Okay. From here, what I'll also go into is a little bit of hamstrings. Your hamstrings were actually quite tight, so actually driving into. Yeah, exactly.
Speaker B: So really kind of dig into like deeper. It's been very. It's just, I'd say one of the
Speaker A: things that I really want to dive
Speaker B: deeper is we're covering on five hand
Speaker A: stuff, but actually going into more.
Speaker B: Yeah.
Speaker A: Sorry, what you said.
Speaker B: Oh, the. Oh yeah. So the church stuff.
Speaker A: Feel good with this pressure.
Speaker B: I might fall asleep,
Speaker A: wake you up soon.
Speaker B: Some of my.
Speaker A: Came into.
Speaker B: I've known like. And then like some other, other clinicians too.
Speaker A: I have a lot of their foundation
Speaker B: work based off like his work, but I never actually go into his work.
Speaker A: Yeah, yeah.
Speaker B: I'm not, I'm not. I have no, no kids are. It's like he's not the source. You mean maybe gray, the way that
Speaker A: he, he goes about materialist.
Speaker B: He's like working some of Gary's stuff. Yeah, the same stuff.
Speaker A: Yeah.
Speaker B: But in a not even like clean way.
Speaker A: Yeah.
Speaker B: That's kind of where I'm I think
Speaker A: I read source of this concept.
Speaker B: Yeah, yeah, I saw you like I might, I might try to be biased.
Speaker A: I'm.
Speaker B: I'm the most like I, I wouldn't say unbiased, but I'm definitely more like what works for the person. Yeah. And then, and then like if I want, you know, is with like, like so. So you know you can get away with the every grade stuff like that at this level. But when you're going up there. Yeah, so exactly. Yeah.
Speaker A: So it's like how do you, how
Speaker B: do we make more accurate, more source as quick as possible?
Speaker A: And obviously from there you can make your own variation. I would actually prefer that you drink from the source and you make your own variation. Yeah.
Speaker B: Versus versus.
Speaker A: You have like he takes from the source, makes his own variation from here. Let's have you lay face off.
Speaker B: Yeah. That actually makes it messy. It does, yes. And that, that's something too.
Speaker A: What time are you seeing last night?
Speaker B: So like now it's like what time
Speaker A: does your day usually. Just nap throughout the day or just straight up? Like 2 to 7?
Speaker B: The most efficient sleep. Mainly when it goes up. It's interesting.
Speaker A: Got it, got. If you were to go double arm out here and you just turn down and hold that front of the shoulder, hold this knee right here.
Speaker B: Like at the moment it feels fine. Like one. I feel like once I rotate back up. Yeah, right here. It's like the transition.
Speaker A: Would you say that in this position, the front of the shoulder station that knee.
Speaker B: Feel that hard? Yeah, yeah. Cuz I, I think when I did this before there was like a tightness or something. Exactly, yeah.
Speaker A: How this makes sense in terms of like how it's reacting to this is often times in a shoulder that's been, you know, stiff from like a stabilization surgery, if we start to improve some motion and the amount greater than what the shoulder has been able to like control for because you have, you can feel some of like those sensations kind of getting into it. Not necessarily grabbing just tells me exactly kind of like where your shoulder is at probably. And at a point where we need to pulse restore motion but also restore control as well. Okay, from here what I'll have you do is go ahead and put your feet together and reach down towards your toes. Let me know how that feels.
Speaker B: It's not any all, it's just that like it feels this accurate maybe like how people cheat very slightly better than before.
Speaker A: Here's what I'm going to have you do. You're going to have a seat here. You're going to work on one other release before I show you something.
Speaker B: Exercise or not.
Speaker A: Extend your knee fully. What you're going to do.
Speaker B: You see how like you feel that? Yeah.
Speaker A: What you're going to do in a sec is you're going to start to. For example, in your case we don't have. And you want to bend for you as much as you want.
Speaker B: Yeah.
Speaker A: And then my job. Some of the muscles on both sides of your spine get that area to bend forward a little bit. Might, might not. So from here just going to put this.
Speaker B: Maybe not.
Speaker A: Cool.
Speaker B: Yeah.
Speaker A: Yeah.
Speaker B: And the final thing, I'll tell you simply.
Speaker A: Yeah. And hold this, hold this, hold this. I'm gonna do that two more times and we'll see if that changes any of the types of. What's up, Carl? I don't know if you met Paul. Kyle.
Speaker B: You gotta get on a ladder to hold back.
Speaker A: And hold, hold, hold.
Speaker B: Okay.
Speaker A: That's it. That's all you need to know for me. Hanging in there. You guys should charge up extra by 8. Come back up. True. What are you feeling? When I'm getting down.
Speaker B: So it generally just feels good. Good, good. And then like quite painful. Okay.
Speaker A: I'll adjust my pressure here.
Speaker B: I can deal with this a little
Speaker A: more longer for you.
Speaker B: Right.
Speaker A: So people have all these for sure. That area quite stiff and that's why you're feeling.
Speaker B: But it's not the same because it's not how the mechanics work. But because it's so complicated, people kind of think that.
Speaker A: Toby, stand back up. Feet together. Let's go. What the recipe should feel like. You have all the ingredients but you're. How does that feel to you? Still decently similar.
Speaker B: One small step better.
Speaker A: Okay. Okay. Let's have you more or less throw your shirt back on.
Speaker B: Sure.
Speaker A: Go over one or two motions just to kind of make a little bit more.
Speaker B: Sure.
Speaker A: Exactly. Exactly. Anytime. I'm just going to grab something. Right, right.
Speaker B: Cost of greatest. So this is where like the living room is.
Speaker A: This is where the couch helps to get. Get a little bit of a deeper stretch out the hamstring muscle out of your lower back. So what we're going to do is you're just going to sink down. You're not going to like crunch all the way down or anything crazy. You're just going to hang here. You're going to inhale and on the exhale, see if you can relax into
Speaker B: it a little bit more.
Speaker A: And then from here, as you inhale again, I don't want you to start to stand up or anything. You're going to stay down as you inhale framework.
Speaker B: It's like, it's like.
Speaker A: And then exhale.
Speaker B: The reason why I created like that
Speaker A: is actually because it allows you to kind of build for five deep breaths and then from there we'll see if that improves any of your mobility.
Speaker B: How do you, you know, someone comes in cars on fire.
Speaker A: I mean, how do you mitigate that? The whole world is really good at that. Physical power massage. It's like however you want to do that really. The exercise, massage or adjustment, you can mitigate fire. Then phase two and three.
Speaker B: Together. Together.
Speaker A: Phase three is essentially, let's reorganize the quantitation wise. And phase three is like, let's reorganize how you drive the car. Right. And then phase four is pay just for the massive.
Speaker B: And that's where traditional strength and conditioning is really develop.
Speaker A: And phase five is you use your
Speaker B: car for basketball card game, whatever you mean.
Speaker A: So one hamstring shaking. So everyone knows stretch somewhere there. All right.
Speaker B: But people don't know how to turn am transition you from a up car to a.
Speaker A: How did you each drive your property? How do I give you access to. Okay, so yeah, let me know if the toe touch goes in.
Speaker B: Yeah, yeah. Anyone can modulate as far as like. Sure, sure. Exactly. I'm warmed up a bit more.
Speaker A: I'd say the curvature here is also slightly improved to. Because your spine has been like tight for. For so long, it's going to take some time to actually get the. The joint changes and also the muscular relaxation. So for this generally it's kind of like at least once a day to get things people who are able to make. I don't know, we're getting changes.
Speaker B: I'm doing a lot of analogies
Speaker A: from here. Let's get into something for your shoulder. Come on. You merge everything here. You become cool.
Speaker B: And what is that exercise called?
Speaker A: Yeah, that is called the beast position Scat press. So this is kind of like a quadruped now. For whatever reason, when people start both their knees, people start to call it like a beast audition. I guess they think it sounds cool. I think it sounds like kind of cheesy, but yeah, let's come on back here. We'll do a little bit of a recap here. So overall, what do you think of the session and how's it compares to, you know, the things that you've been trying out on your own so far?
Speaker B: I mean, I said the session, I think was helpful for me to just kind of think critically about my body. For the first time in a long time. So I think that's helpful. Just looking at the difference between doing one exercise literally for like two minutes and like how a very specific pain goes away way, like gives me some sense of like, objective progress. I feel like a lot of things with the body are like, just really hard to measure.
Speaker A: Yeah.
Speaker B: So it's hard for me to say like, yes, this is like something that I should dedicate the time to. But yeah, I really just need to. I'm sure we'll like, come to terms with the fact that like I have to do these types of things or it's just, it's. It's not even staying the same. I'm just continuing to.
Speaker A: Right, right. Yeah. It's hard to commit to something, especially if the things that you're doing for it is hard to kind of like reflect that and be.
Speaker B: Yeah.
Speaker A: Now in order for, you know, to, to. For you to, you know, get back to you realizing your full motions of the shoulder, get back to, you know, working out before when you're ready for it and also, you know, toss your kids around for the years to come. The things that we'll need to improve is restoring some range of motion which, you know, the hands on stuff actually started to improve this motion. And from there on, the thing that needs to be progressed is actually some strengthening. So the hands on stuff improved range of motion. We started strengthening some of the shoulder blade stabilizers. What will need to occur is after, outside of getting the range of motion we're strengthening, but we're also going to different types of pressing positions to also rebuild a little bit more strength around the stabilizers, but getting a little bit more functional. Because if you're able to do that, eventually we're going to progress to quicker kind of like push ups and ball tosses in order to get even more stability around the shoulders, especially, you know, years after your stabilization surgery. So stabilization surgery, they probably put a lot of good like connections within the shoulder, but you just don't have as much of like the muscle stabilizers right now. Yeah, the low back is actually gonna take is the thing that's probably going to take a little bit longer given how sticky it is based on. But something like that. Your range of motion that we're able to slowly start to restore is the toe touching. And with this one, the thing that we used was a toe elevated toe touch. And I started to increase a little bit more joint motions there. The things that we'll need to do to improve it over the long term is Doing more things that is actually able to relax the hamstrings and back. So we're talking about going to different types of like slouch positions and going into like a slouch squat to really, really fully open that area up. We'll do a little bit of reflect frog breathing where we're on hands and knees. After that, we'll start with some strength work in that position as well. Usually with something like this, like shoulder back to, you know, 90 plus percent probably takes around four to five months, but the low back tends to take closer to around like six to seven months. How does that all sound to you? Like, what's your head at it? Does that sound like the solution that you have been trying to fish for?
Speaker B: Yeah, I do think for me it's like it is a matter of what can I do probably every day at home.
Speaker A: Yeah.
Speaker B: Just so that there's like no excuse for me not to do it. Like coming here. It's like, you know, it's 30 minutes to get here, our session, 30 minutes to get back.
Speaker A: Yeah. So it's a big chunk of that.
Speaker B: Yeah. And I do think, you know, like figuring out like, what the frequency is for me to like, come to somebody like you or Kyle. Kyle or Kayla and, you know, really assess, like true progress would be helpful, but like, it's also the discipline put into it every day. Like the. The small chunks of things that I can. I can consistently do or not do.
Speaker A: Yeah, that will.
Speaker B: That will make the difference.
Speaker A: Yeah. I mean. Yeah, you're starting to realize, you know, the. The what's kind of like at stake, you know, if you don't take care of this long term. I know that you're a busy guy in general, so, you know, you're looking at the different things of like, what it could potentially look like. Like, especially if it takes like 30 minutes to get here. With, you know, with something like this, I tend to make kind of just general best case recommendations for you, which would start off, you know, at once a week for the next six weeks to get the shoulder, to improve some mobility and start to some strengthen some of those pushing movements. And then it's every other week for the new for the following ones. Does that sound like something that could work for you? You can, you can like playlist however you want them to interpret.
Speaker B: Yeah.
Speaker A: Yeah.
Speaker B: I mean, I think it would be practically hard for me to make it here every single week, but I could probably do it monthly, realistically.
Speaker A: Got it, got it, got it. Then could I make the suggestion of setting up a couple of those monthly visits around, like two to three months ahead of time. My schedule actually tends to get fuller, like four weeks in advance. So that might be kind of like the perfect change to start to set you up. So that way you have a time that works best for your busy schedule. But also we don't have to chase each other's schedules. Yeah.
Speaker B: Yeah, that sounds great. Cool.
Speaker A: Great. Well, the follow up email is going to be sent to you, and then I usually send up a follow up text the day after. Great.
Speaker B: Cool.
Speaker A: All right.
Speaker B: Appreciate the time.`;

// ---------------------------------------------------------------------------
// Visit 1 (thin) — the two-paths beat. Same patient, same day, but a "chitchat"
// version: the conversation never settles into assessment. No measurements get
// stated, so extraction has almost nothing to code — and that's the point.
// ---------------------------------------------------------------------------
const SCOTT_V1_THIN = `Speaker A: Hey Scott, good to see you. How's the week been?
Speaker B: Busy, honestly. The startup's across like seven time zones so I'm kind of always on. But good busy.
Speaker A: Nice, nice. And the weekend?
Speaker B: My daughter's first birthday is coming up. We rented a bouncy castle, inviting half the neighborhood. Should be chaos.
Speaker A: That's awesome. She'll have no idea but it'll be fun for everyone.
Speaker B: Exactly. We met a bunch of the parents on walks, so it's kind of a way to say hi.
Speaker A: Love it. So what brings you in today, anything in particular bugging you?
Speaker B: Honestly nothing major today. I'm just generally pretty stiff, you know? Tight all over. My back gets cranky once in a while but it's fine right now.
Speaker A: Gotcha. Anything you're hoping to get out of coming in?
Speaker B: I don't know, just feel a bit better I guess. I used to play a lot of sports and I've kind of let it all go.
Speaker A: Yeah, that happens. Life gets busy.
Speaker B: Totally. And work is the kind of thing where I actually enjoy it, so it's easy to just keep going and skip the gym.
Speaker A: For sure. Well, we can take a look and just get you moving a little today, keep it light.
Speaker B: Sounds good. No pain or anything right now, so whatever you think.
Speaker A: Cool. Let's just do some easy movement and chat through it. How's the sleep been with the little one?
Speaker B: Up and down, you know how it is. But we're managing.
Speaker A: Good. Alright, let's get started, nice and easy.
Speaker B: Appreciate it.`;

// ---------------------------------------------------------------------------
// Visit 2 (rich) — ~6 weeks out. Phase 1 holding, beginning phase 2. The
// clinician restates the same gradeable measures with improved numbers so
// extraction yields the first clean trend point.
// ---------------------------------------------------------------------------
const SCOTT_V2_RICH = `Speaker A: Scott, good to see you back. It's been about six weeks. How'd the home program go?
Speaker B: Better than I expected, honestly. The toe-elevated toe touch and the couch hamstring stretch I actually did most days. I'd say four or five days a week. The shoulder stuff I was less consistent on.
Speaker A: That's a great start, especially given how busy you are. Any flare-ups? You mentioned you throw your back out every few months.
Speaker B: None this stretch. And I tossed Alexandra in the air at her birthday party way more than my usual three or four times, so that felt like a win.
Speaker A: Love that. Let's reassess so we can see it on paper. Feet together, reach down toward your toes for me.
Speaker B: Yeah, this already feels easier than last time.
Speaker A: It does. Last visit your fingertips capped out around mid-shin. Today you're getting down to about a hand's width from the floor — call it fifteen centimeters off the ground, where you were closer to twenty-two before. The lumbar curve is rounding a little better too.
Speaker B: Oh nice, that's motivating.
Speaker A: Let's check the shoulder. Lie face down for me, I'll measure internal rotation. Okay — you're at about fifty-two degrees on the right now. We started at forty-five, and we're shooting for that sixty-to-seventy range, so that's real movement.
Speaker B: That's the one that was waking me up at night.
Speaker A: Right, and how's the sleep been on it?
Speaker B: Better. Maybe one bad night in the last two weeks instead of most nights.
Speaker A: Good. Reach up your back for me — last time that was limited by about four spinal segments. Today you're closer to three. And that little one-out-of-ten pinch on the scarecrow test?
Speaker B: Barely there today. Maybe nothing.
Speaker A: Perfect. So here's where we are: range of motion is the lever and it's moving. We're going to start layering in some control and strength work now — that's phase two. Keep the daily mobility, and I'll add a couple of light pressing drills for the shoulder stabilizers.
Speaker B: As long as it's stuff I can do at home, I'm in.
Speaker A: All home-friendly. Same goal — get you back to racket sports without having to deeply warm up first, and keep tossing Al around for years.
Speaker B: That's the dream. Let's keep it going.`;

// ---------------------------------------------------------------------------
// Visit 3 (rich) — ~3 months out. Phase 2 → 3, the trend "pops". Strength work
// is the headline; the measures keep climbing.
// ---------------------------------------------------------------------------
const SCOTT_V3_RICH = `Speaker A: Scott, welcome back. We're about three months in now. How are things feeling day to day?
Speaker B: Genuinely different. The daily mobility is just a habit now, I don't even think about it. And I played a full set of pickleball last weekend with no warm-up dread, which a few months ago would have been a no.
Speaker A: That's huge — that was one of your headline goals. Any back episodes?
Speaker B: Zero. Knock on wood. I put on my pants this morning without that little flinch I used to brace for.
Speaker A: Let's get it on paper. Toe touch first.
Speaker B: Here — I'm basically at my ankles now.
Speaker A: You are. Fingertips to about six centimeters off the floor today, down from fifteen last visit and twenty-two at the eval. The lumbar segments are actually moving when I press now, not locked.
Speaker B: That whole region used to feel like concrete.
Speaker A: Shoulder next, face down. Internal rotation is reading right around sixty degrees on the right. We started at forty-five — you're into the target band now.
Speaker B: That's wild. The night pain is just gone.
Speaker A: And reach behind the back — you're down to about two spinal segments of limitation, from four. Scarecrow test?
Speaker B: No pain. Zero out of ten.
Speaker A: So mobility's restored enough that strength is now the main project — this is phase three, reorganizing how you load it. We'll progress the pressing into quicker push-ups and some ball tosses to build stability around that shoulder, since it's years out from the stabilization surgery and still light on muscle support.
Speaker B: I actually feel strong enough to want that now.
Speaker A: Exactly the point. Keep the mobility as maintenance, and we'll lean into loading. Next stop is getting you confidently back into tennis and lifting Al without a second thought.
Speaker B: Let's do it.`;

// ---------------------------------------------------------------------------
// Visit 4 (rich) — ~5 months out. Phase 4/5, return to sport. Measures at or
// near target; the arc closes on the original goals.
// ---------------------------------------------------------------------------
const SCOTT_V4_RICH = `Speaker A: Scott, five months in. Last visit we were leaning hard into strength. How's it held up?
Speaker B: Really well. I've been playing pickleball pretty much every week, got back on a tennis court twice, and the shoulder just handles it. I throw Alexandra in the air until she's the one who's done, not me.
Speaker A: That's the whole reason we started. Let's capture where the numbers landed. Reach for your toes.
Speaker B: This one I'm proud of — flat hands near the floor.
Speaker A: You're touching your toes. Fingertips to the floor, essentially zero centimeters, from twenty-two at the eval. Ten years since you could do that, you told me.
Speaker B: At least. I genuinely didn't think that was on the table.
Speaker A: Shoulder, face down. Internal rotation is right around sixty-seven degrees on the right now — that's in the sixty-to-seventy target we set at the start, up from forty-five.
Speaker B: And no night pain at all anymore.
Speaker A: Reach behind your back — about one spinal segment of difference side to side now, basically symmetric, down from four. Scarecrow's been clean for months. So all four of the measures we tracked moved the direction we wanted: toe touch, shoulder internal rotation, reach-behind-back, and the provocative pain.
Speaker B: Seeing it as actual numbers is what kept me doing the home stuff, honestly.
Speaker A: That's the win. You're in phase five now — using the body for the things you care about. We can drop to a maintenance check-in cadence; the daily habit is yours. Anything you want to add to the goals?
Speaker B: Just keep being able to play and keep up with the kid. Maybe more kids down the line.
Speaker A: Then we keep the strength and mobility ticking and you're set. Great work, Scott — this was all you doing the reps.
Speaker B: Couldn't have measured it without you. Appreciate it.`;

// ===========================================================================
// Raymond — type 2 diabetes (CONTINUOUS shape). Primary-care visits over a
// year. The plan holds HbA1c inside a 6–7.5% band on a quarterly cadence; the
// silhouette is held-in-band → drift out for two consecutive readings → the
// review rule fires → escalate → back in band. Speaker A = clinician (PCP),
// Speaker B = patient (Raymond, 57M). HbA1c is stated at every visit so
// extraction trends it: 7.2 → 8.0 → 8.3 → 7.0.
// ===========================================================================

// Visit 1 — establish ongoing management; set the band + quarterly cadence +
// review rule. HbA1c 7.2% (in band, near the top).
const RAYMOND_V1_RICH = `Speaker A: Raymond, good to see you. It's been about a year since we really sat down with the diabetes — I want to use today to set up how we're going to manage it going forward, not just react to numbers. Sound okay?
Speaker B: Yeah, that works. Honestly I feel fine, which is part of why I don't think about it much.
Speaker A: That's the tricky thing with type 2 — it's quiet until it isn't. You were diagnosed about four years ago, and you've been on metformin, a thousand milligrams twice a day. Are you taking it pretty consistently?
Speaker B: Most days. I'll be honest, if I'm traveling for work I forget the evening one sometimes.
Speaker A: Good to know. Your A1c from last week came back at 7.2 percent. For context, that's a three-month average of your blood sugar. The range we want to hold you in is 6 to 7.5 percent — so 7.2 is inside the band, near the top of it, but where we want you.
Speaker B: Okay. So 7.2 is fine?
Speaker A: It's fine. Here's how I want to frame this: diabetes isn't something we cure and discharge. It's something we manage on an ongoing basis. So the plan is — we keep your A1c inside that 6-to-7.5 band, we check it every three months, and if it drifts out of range we step in and adjust. You don't have to watch the number day to day; that's our job at the check-ins.
Speaker B: I like that better than being told to panic about every meal.
Speaker A: Exactly. Two things hold the number steady: the metformin, and the lifestyle side — what you eat and moving your body. Tell me about a normal day of eating.
Speaker B: I cook a lot. Dinner's usually good — protein, vegetables. Lunch is where it falls apart, I grab whatever's fast. And I've got a sweet tooth in the evenings.
Speaker A: That's honest and it's workable. I'm not going to overhaul your whole life. Let's target the lunch — a little more protein, a little less of the fast carbs — and keep the evening treat smaller, not gone. Are you moving at all?
Speaker B: I walk the dog. Twenty minutes, most nights.
Speaker A: Keep that, and if you can push a couple of those to thirty, even better. So here's the plan: metformin as prescribed, the lunch and walking tweaks, and I'll see you in three months for the next A1c. If it's still in the band, we just keep the rhythm going. If two readings in a row come back out of range, that's when we sit down and actually change something — medication or lifestyle.
Speaker B: Got it. Three months.
Speaker A: Three months. And put a reminder on your phone for that evening metformin.
Speaker B: Deal.`;

// Visit 2 — quarterly check; HbA1c 8.0% (first out-of-range). Flag it, tighten
// habits, order an early repeat rather than wait the full quarter.
const RAYMOND_V2_RICH = `Speaker A: Raymond, welcome back — three months on the dot. How've things been?
Speaker B: Busy. The holidays happened, so you can probably guess.
Speaker A: I had a feeling. Let's look. Your A1c came back at 8.0 percent this time. Last visit you were at 7.2, inside the band. 8.0 is above our upper limit of 7.5 — so this one's out of range.
Speaker B: The holidays and a couple of work trips. The evening metformin definitely slipped on the road.
Speaker A: That tracks. I'm not alarmed by one reading — a stretch of travel and holiday eating will do exactly this. But I don't want to just wait the full three months and hope. Here's what I'd like to do: tighten the metformin back to every dose, get the walking back to most nights, and I'm going to recheck your A1c in about six weeks instead of waiting the whole quarter.
Speaker B: Why the early recheck?
Speaker A: Because of how your plan works — if two readings in a row come back out of range, that tells me it's not just a bad holiday, it's a trend, and we need to change the treatment, not just the habits. The early lab tells us which one this is. If six weeks from now you're back under 7.5, great, we relax. If you're still high, we escalate.
Speaker B: Okay, that's fair. So the ball's in my court for six weeks.
Speaker A: For six weeks. Same metformin, back on schedule, walking most nights, watch the lunches. Let's see where the number lands.
Speaker B: Alright. I can do six weeks.`;

// Visit 3 — the early REVIEW VISIT the rule triggers. Repeat HbA1c 8.3%
// (second consecutive out-of-range) → escalate. The specific med is the
// clinician's decision; the plan only mandates the review.
const RAYMOND_V3_RICH = `Speaker A: Raymond, thanks for coming in early — this is the review visit we talked about. Your repeat A1c from a couple of weeks ago came back at 8.3 percent.
Speaker B: Up from 8.0? I actually tried this time.
Speaker A: I believe you — and that's exactly the useful information. Two readings in a row above the band, 8.0 and now 8.3, and you were making the effort on the second one. That tells me this isn't a holiday blip anymore; the metformin alone isn't holding you in range. Per your plan, two consecutive out-of-range readings is what brings us in to change the treatment, which is why we're here today instead of waiting.
Speaker B: So more medication.
Speaker A: We're going to add a second medication to the metformin, yes. There are a few directions we could go, and I want to pick based on you specifically. You've mentioned wanting to lose a bit of weight, and your kidney numbers and blood pressure are fine. Given that, I'd like to add a medication called empagliflozin — it lowers your blood sugar through the kidneys, it tends to help with weight rather than cause it, and it's protective for the heart and kidneys long term. That's my recommendation over, say, a sulfonylurea, which could drop your sugar too low and nudge weight up.
Speaker B: Any catch?
Speaker A: The main things to know: drink enough water, and there's a small risk of urinary or yeast infections, so keep up on hygiene and tell me if anything comes up. We start it now, alongside the metformin.
Speaker B: Okay, I trust that. What about the check-in schedule?
Speaker A: We go back to the every-three-months rhythm to see if this brings you back into the band. This is the adjustment; now we watch it work. Keep the walking and the lunches going — the medication does its part, but it works better with the habits, not instead of them.
Speaker B: Understood. Empagliflozin, more water, three months.
Speaker A: That's it. This is the plan doing what it's supposed to — we caught the drift and we're responding to it.`;

// Visit 4 — quarterly; HbA1c 7.0% (back in band). Escalation worked; resume the
// cadence. Explicitly NOT a discharge — the management is open-ended.
const RAYMOND_V4_RICH = `Speaker A: Raymond, three months since we added the empagliflozin. Let's see what it did.
Speaker B: I feel good, for what it's worth. Down a few pounds, actually.
Speaker A: I can see that, and the number agrees with you: your A1c is 7.0 percent. You were at 8.3 at the review visit — 7.0 is comfortably back inside the 6-to-7.5 band.
Speaker B: So it worked.
Speaker A: It worked. The second medication was the right call, and the walking and the weight coming down helped it along. This is exactly the outcome the plan is built for — we saw two readings drift out, we escalated, and now you're back in range.
Speaker B: So are we done?
Speaker A: This is the part I want to be clear about — we're not "done" in the discharge sense. Diabetes is ongoing; we keep managing it. What changes is that you're back to steady-state, so we go back to the routine: A1c every three months, hold you in the band, and we only step in again if two readings drift out. No new medication today, nothing to add — just keep doing what you're doing.
Speaker B: Keep the empagliflozin and the metformin both?
Speaker A: Both, same doses. Keep the water up, keep walking the dog. I'll see you in three months for the next check.
Speaker B: Sounds good. Honestly this is less stressful than I thought it'd be.
Speaker A: That's the whole idea — steady beats dramatic. See you in three months.`;

// ===========================================================================
// Nadia — chronic migraine (EPISODIC shape). Headache-clinic visits. A standing
// preventive baseline punctuated by two DIFFERENT event types with different
// responses: an acute trigger flare, then a medication-overuse pattern. The
// silhouette is sawtooth, not monotone, and there is no discharge. Speaker A =
// clinician, Speaker B = patient (Nadia, 34F). Monthly headache days are stated
// each visit so extraction trends them: 8 → 12 → 5 → 10.
// ===========================================================================

// Visit 1 — baseline; start the preventive, the acute (flare) plan, and the
// diary. ~8 headache days/month, severity ~5–6/10.
const NADIA_V1_RICH = `Speaker A: Nadia, thanks for coming in. I've read through your intake — you've had migraines for years, but they've picked up recently. Walk me through where things are now.
Speaker B: Yeah. They've always been around, but the last six months they're more frequent. I'd say I get a headache maybe eight days a month now, and a few of those are the full migraine — light hurts, I get nauseous, I have to lie down.
Speaker A: When you get the bad ones, how would you rate the pain, zero to ten?
Speaker B: The bad ones are like a five or six. Occasionally worse. The everyday ones are more of a dull three.
Speaker A: Okay. And what are you taking when one hits?
Speaker B: Ibuprofen mostly. Sometimes it helps, sometimes I just ride it out.
Speaker A: Here's how I want to approach this, because chronic migraine is really a two-part problem. There's the day-to-day prevention — trying to bring that eight-days-a-month number down — and there's the acute plan, what you do when one actually hits. We build both.
Speaker B: Okay, that makes sense. I've only ever done the "wait it out" thing.
Speaker A: Let's fix that. On the prevention side, I want to start you on a daily medication — we'll use propranolol, a low dose to begin, and titrate up. It's one of the best-studied migraine preventives. It won't do much the first couple of weeks; these take six to eight weeks to really show their effect, so don't judge it early.
Speaker B: Got it, daily. And when I actually get one?
Speaker A: That's the acute plan. I'm giving you a triptan — sumatriptan — for the migraine-level attacks, the fives and sixes. The key is to take it early, when it's coming on, not after you've white-knuckled it for four hours. But — and this matters — no more than two, maybe three days a week on the triptan. Overusing the acute medication can actually cause more headaches, and we'll talk about that if it ever comes up.
Speaker B: Okay, early and not too often.
Speaker A: Right. Last piece: I want you keeping a headache diary. Just the days you get one and how bad. Your triggers matter — you mentioned your work is a lot of screen time and deadlines. Sleep and stress are the big two for most people. The diary tells us what's setting them off and whether the preventive is working.
Speaker B: I can do that. There's an app I think.
Speaker A: Perfect. So: propranolol daily, sumatriptan for the bad ones taken early and sparingly, the diary, and I'll see you back in about a month — sooner if you have a bad stretch. We're aiming to get those headache days down over the next couple of months.
Speaker B: Thank you. This is the first time it's felt like a plan.`;

// Visit 2 — acute FLARE (event type 1). A trigger cluster spikes to ~12 headache
// days, severity up to 9/10. Response: optimize abortive timing, step the
// preventive, short-interval follow-up.
const NADIA_V2_RICH = `Speaker A: Nadia, you called and we got you in — you said this has been a bad stretch. Tell me.
Speaker B: The last few weeks have been rough. We had a product launch, I was barely sleeping, and the migraines just exploded. I've had a headache almost every other day, and two of them were the worst I've ever had — like a nine. I actually left work early twice.
Speaker A: I'm sorry, that sounds miserable. Let's count it — over the past month, how many headache days total, would you say?
Speaker B: I looked at the diary before coming. Twelve days this month. Up from the eight when we started.
Speaker A: Okay. So this is a flare — a cluster driven by the launch, the sleep loss, and the stress all stacking up. This is exactly the kind of episode the plan is built to respond to, so let's respond. First: how's the propranolol been? Are you still taking it daily?
Speaker B: Every day, yeah. But it doesn't feel like it's doing anything.
Speaker A: It's only been about five weeks, and a flare like this can punch right through a preventive that hasn't fully ramped yet — that doesn't mean it's failing. I'm going to bump the propranolol up to the next dose, which was the plan anyway. The more urgent thing is getting you through this flare. When you took the sumatriptan for those nine-out-of-ten days, did you take it early?
Speaker B: No, because I didn't want to "waste" it. By the time I took it I was already wrecked.
Speaker A: That's the single most important fix. The triptan works dramatically better taken in the first hour. I want you to treat early — the moment you know it's a real one. For this flare specifically, let's also make sure you have an anti-nausea option, and I want you protecting sleep like it's medication, because right now it is.
Speaker B: So push the sleep, take the triptan early, higher propranolol.
Speaker A: Exactly. And because you're in a flare, I want to see you back in two weeks, not a month — I want eyes on this while it's active. If the triptan days start creeping past two or three a week, call me before then.
Speaker B: Okay. Two weeks.
Speaker A: Two weeks. We'll get you back down.`;

// Visit 3 — the flare has SETTLED back to baseline. ~5 headache days/month,
// severity ~4/10. Not a discharge — return to standing monitoring, the flare
// rule stays armed for next time.
const NADIA_V3_RICH = `Speaker A: Nadia, two weeks — well, a bit more. How are things since the flare?
Speaker B: So much better. The launch is over, I'm sleeping again, and honestly the propranolol feels like it finally kicked in. The last few weeks have been quiet.
Speaker A: Let's put a number on it. Pull up the diary — headache days this past month?
Speaker B: Five. And none of them were the scary kind — the worst was maybe a four. Mostly the dull background ones.
Speaker A: That's real progress. You started at eight a month, the flare took you up to twelve, and now you're at five with the severity way down. The preventive is doing its job now that it's at full dose and the triggers settled.
Speaker B: It's such a relief. I was worried that flare was my new normal.
Speaker A: That's the important thing about how we manage this — the flare wasn't a failure, and this quiet stretch isn't the finish line either. This is your baseline, and it's a good one. We keep the propranolol going, keep the diary going, and keep the sumatriptan for the occasional bad day.
Speaker B: How often should I be coming in now that it's calmer?
Speaker A: We stretch it back out — I don't need to see you every two weeks when you're stable. Let's go back to a monthly check-in on the diary, and the standing rule holds: if you hit another bad stretch like the launch, we pull you in early and treat the flare, same as we just did. You don't wait it out.
Speaker B: Okay. Five days a month I can live with.
Speaker A: That's the goal — keep it there or lower. Keep treating the bad ones early. See you in a month.`;

// Visit 4 — a DIFFERENT event type: medication-overuse headache. ~10 headache
// days/month, but the mechanism is overuse, not a trigger flare — so the
// response inverts (taper the abortive, lean on prevention + a bridge). This is
// what makes the shape episodic: the plan holds multiple event responses.
const NADIA_V4_RICH = `Speaker A: Nadia, good to see you. Last month you were at five headache days and feeling good — how's it been since?
Speaker B: That's the thing, it's crept back up. Not like the launch flare, but I'm getting headaches more days again. And I feel like I'm taking the sumatriptan a lot.
Speaker A: Let's look at both numbers, because this might be a different problem than the last flare. Headache days this past month?
Speaker B: About ten. But it's a different feeling — it's not the big knock-me-down ones, it's this constant low-grade daily headache that's there when I wake up.
Speaker A: And the sumatriptan — how many days a week are you taking it, honestly?
Speaker B: More than you told me. Probably four or five days a week. Whenever I feel one starting, because it works.
Speaker A: Okay. This is a different event than the launch flare, and it's an important one to catch — I think you've developed what we call medication-overuse headache. The triptan is working in the moment, so you're reaching for it more, but taken that often it starts causing rebound headaches — that daily, wake-up-with-it pattern you're describing. Ten headache days, but the mechanism here is the overuse, not a trigger flare.
Speaker B: Wait — the thing that helps is causing them?
Speaker A: When it's used more than about two or three days a week, yes. It's a known trap, and it's fixable. The response here is different from a flare: instead of leaning harder on the acute medication, we pull back on it. I want to taper the sumatriptan down to no more than two days a week — we may go through a rough patch of a week or two as your brain readjusts, and I won't lie, it can feel worse before it feels better.
Speaker B: That sounds awful, but okay.
Speaker A: To get you through it, we lean on the preventive side — we keep the propranolol, and I'll add a short bridge to cover you during the taper so you're not white-knuckling it. This is exactly why we keep managing this as an ongoing thing rather than declaring victory — different episodes call for different responses, and this one is the overuse pattern, not a trigger flare.
Speaker B: So cut back the triptan, bridge medication, keep the propranolol.
Speaker A: That's the plan. Keep the diary going through the taper — I want to see those overuse days come down and the daily headaches lift with them. Let's talk in two weeks to see how the taper's landing.
Speaker B: Okay. I'm glad this has a name, at least.
Speaker A: It does, and it has a fix. We'll get you back to that five-day baseline.`;

// The library. Ordering here is incidental — the picker groups by journey and
// sorts by visit (then rich-before-thin), so authoring order doesn't matter.
export const TRANSCRIPT_LIBRARY: TranscriptLibraryEntry[] = [
  {
    id: 'scott-v1-rich',
    journeyId: 'scott',
    specialty: 'Physical therapy',
    persona: 'Scott · 43M',
    visit: 1,
    phase: 'Phase 1 · Restore range of motion',
    variant: 'rich',
    title: 'Visit 1 — Initial evaluation',
    summary:
      'Full Myo eval: post-stabilization shoulder + longstanding lumbar stiffness. States shoulder IR ~45°, reach-behind-back limited ~4 segments, limited toe-touch, 1/10 provocative pain. Goals drawn out richly.',
    tags: [
      'eval',
      'evaluation',
      'initial',
      'shoulder',
      'internal rotation',
      'low back',
      'lumbar',
      'toe touch',
      'forward flexion',
      'hamstring',
      'racket sports',
      'pickleball',
      'tennis',
      'rich',
      'good path',
    ],
    text: SCOTT_V1_RICH,
  },
  {
    id: 'scott-v1-thin',
    journeyId: 'scott',
    specialty: 'Physical therapy',
    persona: 'Scott · 43M',
    visit: 1,
    phase: 'Phase 1 · Restore range of motion',
    variant: 'thin',
    title: 'Visit 1 — Eval (chitchat variant)',
    summary:
      "Two-paths beat: the same eval that never settles into assessment. Small talk, no measurements stated — extraction degrades, and that's correct.",
    tags: [
      'eval',
      'evaluation',
      'chitchat',
      'thin',
      'two paths',
      'small talk',
      'shoulder',
      'low back',
    ],
    text: SCOTT_V1_THIN,
  },
  {
    id: 'scott-v2-rich',
    journeyId: 'scott',
    specialty: 'Physical therapy',
    persona: 'Scott · 43M',
    visit: 2,
    phase: 'Phase 2 · Reorganize & control',
    variant: 'rich',
    title: 'Visit 2 — 6-week reassessment',
    summary:
      'First trend point: shoulder IR ~52°, toe-touch ~15 cm off floor, reach-behind-back ~3 segments, provocative pain ~0. Starts layering control/strength.',
    tags: [
      'reassessment',
      'follow-up',
      'progress',
      'shoulder',
      'internal rotation',
      'toe touch',
      'trend',
      'phase 2',
      'rich',
    ],
    text: SCOTT_V2_RICH,
  },
  {
    id: 'scott-v3-rich',
    journeyId: 'scott',
    specialty: 'Physical therapy',
    persona: 'Scott · 43M',
    visit: 3,
    phase: 'Phase 3 · Strength',
    variant: 'rich',
    title: 'Visit 3 — 3-month reassessment',
    summary:
      'Trend pops: shoulder IR ~60° (into target band), toe-touch ~6 cm off floor, reach-behind-back ~2 segments, pain 0. Strength is now the headline; back to pickleball.',
    tags: [
      'reassessment',
      'follow-up',
      'progress',
      'shoulder',
      'internal rotation',
      'toe touch',
      'strength',
      'trend',
      'phase 3',
      'pickleball',
      'rich',
    ],
    text: SCOTT_V3_RICH,
  },
  {
    id: 'scott-v4-rich',
    journeyId: 'scott',
    specialty: 'Physical therapy',
    persona: 'Scott · 43M',
    visit: 4,
    phase: 'Phase 5 · Return to sport',
    variant: 'rich',
    title: 'Visit 4 — 5-month reassessment',
    summary:
      'Arc closes: shoulder IR ~67° (in target), touches toes (~0 cm), reach-behind-back ~1 segment, pain clean for months. Back to tennis + lifting his kid; drops to maintenance.',
    tags: [
      'reassessment',
      'follow-up',
      'discharge',
      'maintenance',
      'shoulder',
      'internal rotation',
      'toe touch',
      'return to sport',
      'tennis',
      'trend',
      'phase 5',
      'rich',
    ],
    text: SCOTT_V4_RICH,
  },
  {
    id: 'raymond-v1-rich',
    journeyId: 'raymond',
    specialty: 'Primary care',
    persona: 'Raymond · 57M',
    visit: 1,
    phase: 'Baseline · establish control band',
    variant: 'rich',
    title: 'Visit 1 — Establish ongoing management',
    summary:
      'Type 2 diabetes framed as ongoing management, not a cure. Sets the HbA1c 6–7.5% band, the quarterly cadence, and the review rule. HbA1c 7.2% (in band).',
    tags: [
      'diabetes',
      'type 2',
      'hba1c',
      'a1c',
      'continuous',
      'control band',
      'baseline',
      'metformin',
      'primary care',
      'endocrine',
      'rich',
    ],
    text: RAYMOND_V1_RICH,
  },
  {
    id: 'raymond-v2-rich',
    journeyId: 'raymond',
    specialty: 'Primary care',
    persona: 'Raymond · 57M',
    visit: 2,
    phase: 'Monitoring · first out-of-range',
    variant: 'rich',
    title: 'Visit 2 — Quarterly check (drifts out of band)',
    summary:
      'First out-of-range reading: HbA1c 8.0% after a holiday/travel stretch. Flags it, tightens habits, orders an early repeat rather than wait the full quarter.',
    tags: [
      'diabetes',
      'hba1c',
      'a1c',
      'continuous',
      'out of range',
      'drift',
      'quarterly',
      'monitoring',
      'rich',
    ],
    text: RAYMOND_V2_RICH,
  },
  {
    id: 'raymond-v3-rich',
    journeyId: 'raymond',
    specialty: 'Primary care',
    persona: 'Raymond · 57M',
    visit: 3,
    phase: 'Review · two readings out of range',
    variant: 'rich',
    title: 'Visit 3 — Review visit (rule triggered)',
    summary:
      "Repeat HbA1c 8.3% — two consecutive out-of-range readings trip the review rule. Escalates: adds empagliflozin (the clinician's call), resumes quarterly monitoring.",
    tags: [
      'diabetes',
      'hba1c',
      'a1c',
      'continuous',
      'review',
      'escalation',
      'empagliflozin',
      'medication',
      'out of range',
      'rich',
    ],
    text: RAYMOND_V3_RICH,
  },
  {
    id: 'raymond-v4-rich',
    journeyId: 'raymond',
    specialty: 'Primary care',
    persona: 'Raymond · 57M',
    visit: 4,
    phase: 'Monitoring · back in band',
    variant: 'rich',
    title: 'Visit 4 — Back in band',
    summary:
      "HbA1c 7.0% — the escalation worked and he's back inside the band. Explicitly not a discharge: resume the quarterly rhythm, keep managing.",
    tags: [
      'diabetes',
      'hba1c',
      'a1c',
      'continuous',
      'in band',
      'steady state',
      'monitoring',
      'maintenance',
      'rich',
    ],
    text: RAYMOND_V4_RICH,
  },
  {
    id: 'nadia-v1-rich',
    journeyId: 'nadia',
    specialty: 'Neurology',
    persona: 'Nadia · 34F',
    visit: 1,
    phase: 'Baseline · standing care',
    variant: 'rich',
    title: 'Visit 1 — Baseline & preventive start',
    summary:
      'Chronic migraine at ~8 headache days/month (severity 5–6). Starts the preventive baseline: daily propranolol, an early-and-sparing triptan flare plan, and a headache diary.',
    tags: [
      'migraine',
      'headache',
      'episodic',
      'baseline',
      'preventive',
      'propranolol',
      'triptan',
      'diary',
      'neurology',
      'rich',
    ],
    text: NADIA_V1_RICH,
  },
  {
    id: 'nadia-v2-rich',
    journeyId: 'nadia',
    specialty: 'Neurology',
    persona: 'Nadia · 34F',
    visit: 2,
    phase: 'Event · acute flare',
    variant: 'rich',
    title: 'Visit 2 — Acute flare',
    summary:
      'A trigger flare (launch + sleep loss) spikes to ~12 headache days/month, severity up to 9. Flare response: fix abortive timing, step the preventive, short-interval follow-up.',
    tags: [
      'migraine',
      'headache',
      'episodic',
      'flare',
      'event',
      'trigger',
      'acute',
      'sumatriptan',
      'neurology',
      'rich',
    ],
    text: NADIA_V2_RICH,
  },
  {
    id: 'nadia-v3-rich',
    journeyId: 'nadia',
    specialty: 'Neurology',
    persona: 'Nadia · 34F',
    visit: 3,
    phase: 'Baseline · flare settled',
    variant: 'rich',
    title: 'Visit 3 — Flare settled',
    summary:
      'Back to baseline: ~5 headache days/month, severity ~4, preventive now working. Not a discharge — return to standing monitoring, the flare rule stays armed.',
    tags: [
      'migraine',
      'headache',
      'episodic',
      'recovery',
      'baseline',
      'preventive',
      'settled',
      'neurology',
      'rich',
    ],
    text: NADIA_V3_RICH,
  },
  {
    id: 'nadia-v4-rich',
    journeyId: 'nadia',
    specialty: 'Neurology',
    persona: 'Nadia · 34F',
    visit: 4,
    phase: 'Event · medication overuse',
    variant: 'rich',
    title: 'Visit 4 — Medication-overuse pattern',
    summary:
      'A different event type: ~10 headache days/month from medication-overuse (rebound), not a trigger flare. The response inverts — taper the abortive, lean on prevention + a bridge.',
    tags: [
      'migraine',
      'headache',
      'episodic',
      'medication overuse',
      'rebound',
      'event',
      'taper',
      'sumatriptan',
      'neurology',
      'rich',
    ],
    text: NADIA_V4_RICH,
  },
  // Pre-existing single-visit examples, wrapped from TestTranscripts so they
  // surface in the same picker with real titles and summaries.
  {
    id: 'tuberculosis',
    title: 'Tuberculosis workup',
    summary:
      'Weeks of cough now with hemoptysis, night sweats, weight loss, and fatigue after TB exposure and travel; plans chest X-ray and sputum testing.',
    specialty: 'Primary care',
    tags: [
      'tuberculosis',
      'tb',
      'cough',
      'hemoptysis',
      'infectious',
      'exposure',
      'travel',
    ],
    text: TestTranscripts.tuberculosis,
  },
  {
    id: 'diabetesWithMedicine',
    title: 'New type 2 diabetes (with medications)',
    summary:
      'Polyuria, polydipsia, fatigue, blurry vision, and weight loss with a family history; orders fasting glucose + A1C and prescribes metformin 500mg and paracetamol.',
    specialty: 'Primary care',
    tags: [
      'diabetes',
      'type 2',
      'a1c',
      'metformin',
      'thirst',
      'blurry vision',
      'medication',
    ],
    text: TestTranscripts.diabetesWithMedicine,
  },
  {
    id: 'breastCancerSurvivorship',
    title: 'Breast cancer survivorship follow-up',
    summary:
      'First shared-care follow-up after active treatment: anastrozole adherence and joint aches, lymphoedema watch, DXA + annual mammogram, counselling referral.',
    specialty: 'Oncology',
    tags: [
      'breast cancer',
      'survivorship',
      'oncology',
      'anastrozole',
      'mammogram',
      'follow-up',
      'shared care',
    ],
    text: TestTranscripts.breastCancerSurvivorship,
  },
  {
    id: 'rightKneePain',
    title: 'Right knee pain',
    summary:
      'Medial joint-line pain after a curb misstep, with catching and effusion; positive McMurray; progressive knee rehab program.',
    specialty: 'Physical therapy',
    tags: ['knee', 'meniscus', 'mcmurray', 'effusion', 'rehab', 'msk'],
    text: TestTranscripts.rightKneePain,
  },
  {
    id: 'rightHipPain',
    title: 'Right hip pain',
    summary:
      'Insidious deep groin pain with night pain; positive FABER, limited internal rotation; hip mobility + glute strengthening program.',
    specialty: 'Physical therapy',
    tags: ['hip', 'groin', 'faber', 'internal rotation', 'rehab', 'msk'],
    text: TestTranscripts.rightHipPain,
  },
  {
    id: 'rightShoulderPain',
    title: 'Right shoulder pain',
    summary:
      'Painful arc and positive Hawkins-Kennedy after a weekend of overhead painting; rotator cuff impingement, no tear; staged cuff rehab.',
    specialty: 'Physical therapy',
    tags: [
      'shoulder',
      'rotator cuff',
      'impingement',
      'hawkins-kennedy',
      'painful arc',
      'overhead',
      'msk',
    ],
    text: TestTranscripts.rightShoulderPain,
  },
  {
    id: 'thoracicSpinePain',
    title: 'Thoracic spine pain',
    summary:
      'Desk-posture mid-back ache between the shoulder blades; limited rotation, normal neuro exam; thoracic mobility + posture program.',
    specialty: 'Physical therapy',
    tags: ['thoracic', 'mid-back', 'posture', 'desk', 'ergonomics', 'msk'],
    text: TestTranscripts.thoracicSpinePain,
  },
  {
    id: 'pelvicPain',
    title: 'Pelvic pain',
    summary:
      'Four months of deep pelvic/perineal aching pressure, worse with sitting and stress; overactive pelvic floor; relaxation + retraining program.',
    specialty: 'Pelvic health',
    tags: ['pelvic', 'perineal', 'pelvic floor', 'sitting', 'urgency'],
    text: TestTranscripts.pelvicPain,
  },
  {
    id: 'neckPain',
    title: 'Neck pain',
    summary:
      'Laptop-posture cervicalgia with reduced rotation/extension and no radicular signs; cervical rehab + desk ergonomics.',
    specialty: 'Physical therapy',
    tags: ['neck', 'cervical', 'cervicalgia', 'posture', 'ergonomics', 'msk'],
    text: TestTranscripts.neckPain,
  },
  {
    id: 'lowBackPain',
    title: 'Low back pain',
    summary:
      'Months of on-and-off mechanical low back ache; negative straight-leg raise, normal neuro exam; core/hip strengthening maintenance program.',
    specialty: 'Physical therapy',
    tags: ['low back', 'lumbar', 'mechanical', 'flare-up', 'core', 'msk'],
    text: TestTranscripts.lowBackPain,
  },
];

/**
 * A picker section: a journey with its entries ordered by visit (then
 * rich-before-thin), or — when `journey` is null — the ungrouped
 * single-visit entries.
 */
export type TranscriptLibraryGroup = {
  journey: TranscriptJourney | null;
  entries: TranscriptLibraryEntry[];
};

const VARIANT_ORDER: Record<TranscriptVariant, number> = { rich: 0, thin: 1 };

/**
 * Group the library by journey (ordered by visit, rich before thin) with the
 * single-visit entries as a trailing ungrouped section. Two filters compose:
 * a free-text query over title/summary/persona/phase/specialty/tags (every
 * term must match), and a specialty union filter (empty = all). Empty groups
 * are dropped so a search only shows sections with matches.
 */
export function groupedTranscriptLibrary(
  query = '',
  specialties: readonly TranscriptSpecialty[] = [],
): TranscriptLibraryGroup[] {
  const q = query.trim().toLowerCase();
  const matches = (e: TranscriptLibraryEntry): boolean => {
    if (specialties.length > 0 && !specialties.includes(e.specialty)) {
      return false;
    }
    if (!q) {
      return true;
    }
    const haystack = [
      e.title,
      e.summary,
      e.persona ?? '',
      e.phase ?? '',
      e.specialty,
      ...e.tags,
    ]
      .join(' ')
      .toLowerCase();
    return q.split(/\s+/).every((term) => haystack.includes(term));
  };

  const groups: TranscriptLibraryGroup[] = TRANSCRIPT_JOURNEYS.map(
    (journey) => ({
      journey,
      entries: TRANSCRIPT_LIBRARY.filter(
        (e) => e.journeyId === journey.id && matches(e),
      ).sort(
        (a, b) =>
          (a.visit ?? 0) - (b.visit ?? 0) ||
          VARIANT_ORDER[a.variant ?? 'rich'] -
            VARIANT_ORDER[b.variant ?? 'rich'],
      ),
    }),
  );

  groups.push({
    journey: null,
    entries: TRANSCRIPT_LIBRARY.filter(
      (e) => e.journeyId === undefined && matches(e),
    ),
  });

  return groups.filter((group) => group.entries.length > 0);
}
